import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { ApiService } from '@services/api.service';
import { ModeloVistoria } from '../models/modelo-vistoria';
import { ModeloVistoriaListItem } from '../models/modelo-vistoria-list-item';
import { camposModeloVistoriaPadrao } from '../models/campos-modelo-vistoria-padrao';

export function modeloVistoriaVazio(): ModeloVistoria {
  return {
    id: 0,
    nome: '',
    campos: camposModeloVistoriaPadrao(),
    // Item inicial de exemplo, só pra ilustrar os atributos mais comuns — o usuário pode
    // editar/remover à vontade. Itens adicionados depois (pelo botão) nascem sem atributos.
    itens: [
      {
        id: -1,
        nome: 'Exemplo Item Vistoria',
        atributos: [
          { id: 0, nome: 'Foto Geral', ehFoto: true, ordem: 0 },
          { id: 0, nome: 'Material', ehFoto: false, ordem: 0 },
          { id: 0, nome: 'Danos', ehFoto: true, ordem: 0 },
        ],
      },
    ],
    comodos: [],
  };
}

@Injectable({
  providedIn: 'root'
})
export class ModeloVistoriaApiService extends ApiService {

  private readonly baseRoute = '/modelo-vistoria';

  constructor(public override http: HttpClient) {
    super(http);
  }

  public listar(): Observable<ModeloVistoriaListItem[]> {
    return this.get<ModeloVistoriaListItem[]>(this.baseRoute);
  }

  public getById(id: number): Observable<ModeloVistoria> {
    return this.get<any>(`${this.baseRoute}/${id}`).pipe(map(raw => this.mapParaModelo(raw)));
  }

  public criar(modelo: ModeloVistoria): Observable<number> {
    return this.post<number>(this.baseRoute, undefined, this.mapParaRequest(modelo));
  }

  public editar(id: number, modelo: ModeloVistoria): Observable<void> {
    return this.put<void>(`${this.baseRoute}/${id}`, undefined, this.mapParaRequest(modelo));
  }

  public deletar(id: number): Observable<void> {
    return this.delete<void>(`${this.baseRoute}/${id}`);
  }

  public duplicar(id: number): Observable<number> {
    return this.post<number>(`${this.baseRoute}/${id}/duplicar`);
  }

  public getTemplateHtml(id: number): Observable<{ html: string; gerado: boolean }> {
    return this.get<{ html: string; gerado: boolean }>(`${this.baseRoute}/${id}/template-html`);
  }

  public salvarTemplateHtml(id: number, html: string): Observable<void> {
    return this.put<void>(`${this.baseRoute}/${id}/template-html`, undefined, { html });
  }

  public renderizarTemplateHtml(id: number, html: string): Observable<{ html: string }> {
    return this.post<{ html: string }>(`${this.baseRoute}/${id}/template-html/renderizar`, undefined, { html });
  }

  public gerarPdfPreview(id: number, html: string): Observable<Blob> {
    return this.http.post(`${this.baseUrl}${this.baseRoute}/${id}/template-html/pdf`, { html }, { responseType: 'blob' });
  }

  // A API não conhece referência de objeto, só id — um item novo (ainda sem id real) recebe do
  // componente um id negativo único dentro da edição, e é esse valor que os vínculos de cômodo usam
  // pra apontar pra ele. Aqui só espelhamos o que já está no modelo, sem inventar nada na hora de enviar.
  private mapParaRequest(modelo: ModeloVistoria) {
    return {
      nome: modelo.nome,
      campos: modelo.campos.map(c => ({
        id: c.id,
        nome: c.nome,
        tagHtmlPdf: c.tagHtmlPdf || null,
        valorPadrao: c.valorPadrao || null,
      })),
      itens: modelo.itens.map(c => ({
        id: c.id,
        nome: c.nome,
        atributos: c.atributos.map(a => ({
          id: a.id,
          nome: a.nome,
          ehFoto: a.ehFoto,
        })),
      })),
      comodos: modelo.comodos.map(c => ({
        id: c.id,
        nome: c.nome,
        itens: c.itens.map(cc => ({
          itemVistoriaModeloId: cc.item.id,
        })),
      })),
    };
  }

  // Resolve os vínculos de cômodo (que vêm da API só com id/nome) para a referência de objeto que a
  // tela usa, apontando pro item correspondente em modelo.itens.
  private mapParaModelo(raw: any): ModeloVistoria {
    const itens = raw.itens as ModeloVistoria['itens'];
    const itemPorId = new Map(itens.map(c => [c.id, c]));

    return {
      id: raw.id,
      nome: raw.nome,
      campos: raw.campos,
      itens,
      comodos: raw.comodos.map((c: any) => ({
        id: c.id,
        nome: c.nome,
        ordem: c.ordem,
        itens: c.itens.map((cc: any) => ({
          id: cc.id,
          item: itemPorId.get(cc.itemVistoriaModeloId)!,
          ordem: cc.ordem,
        })),
      })),
    };
  }
}
